import { z } from 'zod'
import { nitOptional, optionalString, requiredString } from '../../primitives'
import { MESSAGES } from '../../utils/messages'

/**
 * Estado patrimonial (sección del portal). Espejo del modelo interno IEquityStatement:
 * cónyuge + gastos mensuales detallados + activos (efectivo, bancos, acciones/bonos,
 * vehículos, inmuebles, menaje, mobiliario, cuentas por cobrar, otros) + pasivos
 * (préstamos, tarjetas, deudas comerciales, otros) + acciones en empresas. Datos de
 * debida diligencia (balance personal) → fuente única acá + persistencia estructurada.
 *
 * NOTA: el bloque "info histórica" del modelo interno (Orange County) NO se incluye:
 * es legacy de EE.UU. y no aplica al portal de Guatemala.
 */

/**
 * Monto OPCIONAL. Acepta number o string (el input de moneda guarda el string que
 * escribe el cliente para no perder decimales en progreso) y lo coacciona a number
 * no-negativo; `''`/`null`/`undefined` → undefined. Igual que la sección económica.
 */
const money = () =>
  z.preprocess((v) => {
    if (v === '' || v == null) return undefined
    const n = typeof v === 'string' ? Number(v) : v
    return Number.isNaN(n) ? undefined : n
  }, z.number().nonnegative().optional())

/**
 * Monto OBLIGATORIO dentro de un registro agregado: si el cliente cargó la fila
 * (un banco, un préstamo…), su importe es exigible. Misma coerción string→number.
 */
const requiredMoney = () =>
  z.preprocess((v) => {
    if (v === '' || v == null) return undefined
    const n = typeof v === 'string' ? Number(v) : v
    return Number.isNaN(n) ? undefined : n
  }, z
    .number({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
    .nonnegative())

/** Número entero/decimal OBLIGATORIO (cantidad de títulos, porcentaje de participación). */
const requiredNumeric = (max?: number) =>
  z.preprocess(
    (v) => {
      if (v === '' || v == null) return undefined
      const n = typeof v === 'string' ? Number(v) : v
      return Number.isNaN(n) ? undefined : n
    },
    max != null
      ? z
          .number({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
          .nonnegative()
          .max(max)
      : z
          .number({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
          .nonnegative()
  )

// ──────────────────────────────── Cónyuge ────────────────────────────────

/** Datos del cónyuge (campos opcionales acá; el strict los exige si hasSpouse). */
export const EquitySpouseSchema = z.object({
  name: optionalString(),
  monthlyIncome: money(),
  companyWork: optionalString()
})

// ──────────────────────────────── Gastos ────────────────────────────────

/** Gastos mensuales detallados (todos opcionales: el cliente carga los que apliquen). */
export const EquityExpensesSchema = z.object({
  rentHouse: money(),
  loans: money(),
  vehicles: money(),
  creditCard: money(),
  waterElectricityPhone: money(),
  collegiate: money(),
  food: money(),
  transport: money(),
  insurance: money(),
  recreation: money(),
  other: z
    .object({
      description: optionalString(),
      amount: money()
    })
    .optional()
})

// ──────────────────────────────── Activos ────────────────────────────────

/** Cuenta en institución financiera. */
export const EquityBankSchema = z.object({
  id: optionalString(),
  name: requiredString(),
  number: requiredString(),
  type: requiredString(),
  currency: requiredString(),
  balance: requiredMoney()
})

/** Inversión en acciones/bonos. */
export const EquityStockBondSchema = z.object({
  id: optionalString(),
  class: requiredString(),
  quantity: requiredNumeric(),
  identification: requiredString(),
  nominalValue: requiredMoney(),
  marketValue: requiredMoney()
})

/** Vehículo. */
export const EquityVehicleSchema = z.object({
  id: optionalString(),
  brand: requiredString(),
  line: requiredString(),
  model: requiredString(),
  warranty: z.boolean().default(false),
  term: optionalString(),
  marketValue: requiredMoney()
})

/** Bien inmueble. */
export const EquityRealEstateSchema = z.object({
  id: optionalString(),
  description: requiredString(),
  type: requiredString(),
  mortgager: z.boolean().default(false),
  term: optionalString(),
  marketValue: requiredMoney()
})

/** Conjunto de activos: montos sueltos + arrays por categoría. */
export const EquityAssetsSchema = z.object({
  cash: money(),
  banks: z.array(EquityBankSchema).default([]),
  stocksBonds: z.array(EquityStockBondSchema).default([]),
  vehicles: z.array(EquityVehicleSchema).default([]),
  realEstates: z.array(EquityRealEstateSchema).default([]),
  household: money(),
  furnitureEquipment: money(),
  accountsReceivable: money(),
  other: z
    .object({
      description: optionalString(),
      amount: money()
    })
    .optional()
})

// ──────────────────────────────── Pasivos ────────────────────────────────

/** Préstamo bancario. */
export const EquityLoanSchema = z.object({
  id: optionalString(),
  bank: requiredString(),
  dateConsession: optionalString(),
  destination: requiredString(),
  initValue: requiredMoney(),
  currentBalance: requiredMoney()
})

/** Tarjeta de crédito. */
export const EquityCreditCardSchema = z.object({
  id: optionalString(),
  emitter: requiredString(),
  limitedGranted: requiredMoney(),
  paymentAgreement: z.boolean().default(false),
  currentBalance: requiredMoney()
})

/** Deuda con casa comercial. */
export const EquityCommercialCompanySchema = z.object({
  id: optionalString(),
  commercialCompany: requiredString(),
  dateConsession: optionalString(),
  destination: requiredString(),
  initValue: requiredMoney(),
  currentBalance: requiredMoney()
})

/** Otra cuenta por pagar. */
export const EquityOtherPayableSchema = z.object({
  id: optionalString(),
  description: requiredString(),
  dateConsession: optionalString(),
  destination: requiredString(),
  initValue: requiredMoney(),
  currentBalance: requiredMoney()
})

/** Conjunto de pasivos: arrays por categoría. */
export const EquityLiabilitiesSchema = z.object({
  loans: z.array(EquityLoanSchema).default([]),
  creditCards: z.array(EquityCreditCardSchema).default([]),
  commercialCompanies: z.array(EquityCommercialCompanySchema).default([]),
  otherPayables: z.array(EquityOtherPayableSchema).default([])
})

// ──────────────────────── Acciones en empresas ────────────────────────

/** Participación accionaria en una empresa. */
export const EquityShareCompanySchema = z.object({
  id: optionalString(),
  company: requiredString(),
  percentage: requiredNumeric(100),
  nit: nitOptional()
})

/**
 * Balance personal compartido por AMBOS tipos de persona: gastos + activos + pasivos +
 * acciones en empresas. NO incluye cónyuge: ése es un concepto exclusivo de la persona
 * individual (una empresa no tiene cónyuge — espejo del FEIC jurídico interno, donde el
 * bloque cónyuge del estado patrimonial está deshabilitado).
 */
const equityCore = {
  expenses: EquityExpensesSchema.default({}),
  assets: EquityAssetsSchema.default({}),
  liabilities: EquityLiabilitiesSchema.default({}),
  sharesCompanies: z.array(EquityShareCompanySchema).default([])
}

/** Individual: balance personal + cónyuge (bandera + inclusión en la compra + datos). */
const individualBase = z.object({
  hasSpouse: z.boolean().default(false),
  spouseIncludedInThePurchase: z.boolean().default(false),
  spouse: EquitySpouseSchema.default({}),
  ...equityCore
})

/** Jurídica: sólo el balance personal (sin cónyuge). */
const juridicalBase = z.object(equityCore)

/**
 * Pares "otro" (gastos / activos): la descripción activa el registro. Mientras está vacía,
 * el monto no aplica; cuando tiene contenido, el monto es obligatorio y debe ser mayor a
 * cero. Compartido por individual y jurídica.
 */
function refineOtherPairs(
  data: {
    expenses?: { other?: { description?: string; amount?: number } }
    assets?: { other?: { description?: string; amount?: number } }
  },
  ctx: z.RefinementCtx
) {
  const requireOtherPair = (
    other: { description?: string; amount?: number } | undefined,
    base: 'expenses' | 'assets'
  ) => {
    const hasDescription = !!String(other?.description ?? '').trim()
    const hasPositiveAmount = typeof other?.amount === 'number' && other.amount > 0
    if (hasDescription && !hasPositiveAmount) {
      ctx.addIssue({
        path: [base, 'other', 'amount'],
        code: z.ZodIssueCode.custom,
        message: MESSAGES.REQUIRED
      })
    }
  }
  requireOtherPair(data.expenses?.other, 'expenses')
  requireOtherPair(data.assets?.other, 'assets')
}

/**
 * La forma del estado patrimonial SIN las reglas de submit.
 *
 * `EquityDeclaration`/`EquityDeclarationJuridical` son schemas de ENTRADA: su
 * `superRefine` exige coherencia entre campos al enviar. Un contrato de LECTURA
 * devuelve lo que hay guardado —incluidos los parciales que dejó el auto-save y
 * los expedientes viejos escritos antes de que la regla existiera—, así que
 * consume la base y no la versión refinada.
 */
export const EquityDeclarationShape = individualBase
export const EquityDeclarationJuridicalShape = juridicalBase

/**
 * Schema ESTRICTO individual (submit). Cada registro AGREGADO se valida por su element
 * schema (los importes/identidad pasan a ser exigibles). Si declaró cónyuge, sus datos
 * son obligatorios. El auto-save NO usa esto (guarda parciales vía el subschema Mongoose).
 */
export const EquityDeclaration = individualBase.superRefine((data, ctx) => {
  if (data.hasSpouse) {
    if (!String(data.spouse?.name ?? '').trim()) {
      ctx.addIssue({
        path: ['spouse', 'name'],
        code: z.ZodIssueCode.custom,
        message: MESSAGES.REQUIRED
      })
    }
    if (data.spouse?.monthlyIncome == null) {
      ctx.addIssue({
        path: ['spouse', 'monthlyIncome'],
        code: z.ZodIssueCode.custom,
        message: MESSAGES.REQUIRED
      })
    }
    if (!String(data.spouse?.companyWork ?? '').trim()) {
      ctx.addIssue({
        path: ['spouse', 'companyWork'],
        code: z.ZodIssueCode.custom,
        message: MESSAGES.REQUIRED
      })
    }
  }
  refineOtherPairs(data, ctx)
})

/**
 * Schema ESTRICTO jurídico (submit). Mismo balance personal que el individual pero SIN
 * cónyuge: una empresa no lo tiene. Sólo conserva el refine de los pares "otro".
 */
export const EquityDeclarationJuridical = juridicalBase.superRefine((data, ctx) => {
  refineOtherPairs(data, ctx)
})

export type EquityBank = z.infer<typeof EquityBankSchema>
export type EquityStockBond = z.infer<typeof EquityStockBondSchema>
export type EquityVehicle = z.infer<typeof EquityVehicleSchema>
export type EquityRealEstate = z.infer<typeof EquityRealEstateSchema>
export type EquityLoan = z.infer<typeof EquityLoanSchema>
export type EquityCreditCard = z.infer<typeof EquityCreditCardSchema>
export type EquityCommercialCompany = z.infer<typeof EquityCommercialCompanySchema>
export type EquityOtherPayable = z.infer<typeof EquityOtherPayableSchema>
export type EquityShareCompany = z.infer<typeof EquityShareCompanySchema>
export type EquityDeclaration = z.infer<typeof individualBase>
export type EquityDeclarationJuridical = z.infer<typeof juridicalBase>
